import React, { useEffect, useState } from "react";
import { AppButton } from "site/ui";
import styled from "styled-components";
import { List, Modal } from "antd";
import { BookIcon, TrialIcon } from "common/svg";
import { Link } from "react-router-dom";
import EditProfileModal, { EditProfileModalProps } from "./EditProfileModal";
import { useDeleteAccountStore, usePremiumStore, useProfileStore } from "store";
import AddPaymentModal from "./AddPaymentModal";
import PremiumSubscriptionModal from "./PremiumSubscriptionModal";
import DeleteAccountConfirmation from "./DeleteAccountConfirmation";
import { IPaginateRequest, IUser, IUserCourse } from "utils/types";
import { TabContent, TabContentProps } from "./settings";
import { Loader } from "common";

const Container = styled.div`
  margin: 0 auto;
  padding: 20px 40px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  background-color: #f8f9fa;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ProfileDetailsCard = styled(Card)``;

const AccountSettingsCard = styled(Card)``;

const ContentCard = styled(Card)``;

const SubscriptionCard = styled(Card)``;

const CoursesCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #5575f7;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const UserEmail = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6c757d;
`;

const EditProfile = styled.div`
  color: #5575f7;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  position: absolute;
  top: 20px;
  right: 20px;
`;

const CardTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
`;

const SectionTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #5575f7;
`;

const NavItem = styled.div`
  padding: 12px 0;
  cursor: pointer;
  font-size: 14px;
  color: #6c757d;

  &.active {
    color: #5575f7;
    font-weight: 500;
    border-left: 2px solid #5575f7;
    padding-left: 10px;
    margin-left: -10px;
  }
`;

const FormWrapper = styled.div`
  margin-bottom: 30px;
`;

const NoCoursesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px 0;
`;

const NoCoursesTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
`;

const NoCoursesText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6c757d;
`;

const CourseLink = styled(Link)`
  color: #5575f7;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TrialInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 20px;
`;

const TrialTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
`;

const TrialText = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #6c757d;
`;


export interface ProfileProps extends 
Pick<TabContentProps, "onChangePassword"|"onDeleteAccount">,
Pick<EditProfileModalProps, "onUpdateProfile"> {
  onGetUserCourses: (data: { userId: string; pagination: IPaginateRequest }) => Promise<IUserCourse[]>;
}

export const Profile: React.FC<ProfileProps> = ({
  onChangePassword, onDeleteAccount, onGetUserCourses,
  onUpdateProfile
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState<"profile"|"payment"|"subscription"|"deleteAccount">("profile");
  const [coursePagination, setCoursePagination] = useState<IPaginateRequest>({
    limit: 50, skip: 0
  })
  const { user, getName } = useProfileStore();
  const [userCourses, setUserCourses] = useState<IUserCourse[]>([])
  const [courseLoading, setCourseLoading] = useState(false)
  const { openEditModal } = useProfileStore();
  const { openPremiumModal } = usePremiumStore();
  const {
    isDeleteModalOpen,
    isDeleting,
    closeDeleteModal,
    deleteAccount,
  } = useDeleteAccountStore();

  useEffect(() => {
    if (user) {
      setCourseLoading(true);
      onGetUserCourses({
        userId: user.id,
        pagination: coursePagination
      }).then(setUserCourses).finally(() => setCourseLoading(false));
    }
  }, [  JSON.stringify(user), JSON.stringify(coursePagination)])

  const initial = `${user?.firstName?.charAt(0)||""}${user?.lastName?.charAt(0)||""}`
  return (
    <Container>
      <LeftSection>
        <ProfileDetailsCard>
          <CardTitle>Profile details</CardTitle>
          <ProfileInfo>
            <Avatar>{initial}</Avatar>
            <UserInfo>
              <UserName>{getName()}</UserName>
              <UserEmail>@ {user?.profession || "no profession"}</UserEmail>
            </UserInfo>
          </ProfileInfo>
          <div style={{ display: "flex", marginTop: "10px" }}>
            <div
              style={{
                marginRight: "15px",
                fontSize: "14px",
                color: "#6c757d",
                textTransform: "capitalize"
              }}
            >
              {user?.gender}
            </div>
            <div style={{ fontSize: "14px", color: "#6c757d", textTransform: "capitalize" }}>
              {user?.country}
            </div>
          </div>
          <EditProfile onClick={openEditModal}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 14H14"
                stroke="#5575F7"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.99995 7.5L8.49995 3L12 6.5L7.49995 11H3.99995V7.5Z"
                stroke="#5575F7"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit profile
          </EditProfile>
        </ProfileDetailsCard>

        <TabContent {...{
          onChangePassword, onDeleteAccount
        }}/>
      </LeftSection>

      <RightSection>
        <SubscriptionCard>
          <CardTitle>Subscription plan</CardTitle>
          {user?.onActivePremium ? <>

          </>: <TrialInfo>
            <TrialIcon />
            <TrialTitle>You're on a free trial</TrialTitle>
            <TrialText>
              Your free trial ends on November 15, 2024, giving you access to
              all courses until then. To keep enjoying full access, upgrade to a
              premium plan.
            </TrialText>
            <AppButton variant="primary" fullWidth onClick={openPremiumModal}>
              Subscribe to premium plan
            </AppButton>
          </TrialInfo>}
        </SubscriptionCard>

        <CoursesCard>
          <CardTitle>My courses</CardTitle>

          {courseLoading ? <Loader/> : <>
            {userCourses?.length === 0 ? <NoCoursesContainer>
              <BookIcon />

              <NoCoursesTitle>No courses</NoCoursesTitle>
              <NoCoursesText>
                You currently do not have courses you're taking at the moment.
                Visit our <CourseLink to={"/dashboard"}>courses page</CourseLink>{" "}
                to get started.
              </NoCoursesText>
            </NoCoursesContainer> : 
            
            <List>
              {userCourses?.map((course: IUserCourse) => (
                <List.Item key={course.id}>
                  {course.course?.title}
                </List.Item>
              ))}
            </List>
            }
          </>}
        </CoursesCard>
      </RightSection>
      <Modal footer={null} open={openModal} onClose={() => setOpenModal(false)}>
          {modalType === "profile" && <EditProfileModal {...{
            user: user as IUser, 
            onUpdateProfile,
            onClose() {
              setOpenModal(false);
            },
          }}/>}
          {modalType === "payment" && <AddPaymentModal/>}
          {modalType === "subscription" && <PremiumSubscriptionModal/>}
          {modalType === "deleteAccount" && (
            <DeleteAccountConfirmation
              isOpen={isDeleteModalOpen}
              onClose={closeDeleteModal}
              onConfirm={deleteAccount}
              isLoading={isDeleting}
            />

          )}
      </Modal>
    </Container>
  );
};

export default Profile;
